<script setup lang="ts">
import Modal from "@/ui/components/modal.vue";
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import { useApiClient } from "@/api/client";
import { ref, onMounted, computed } from "vue";
import PricelistUsagesDTO = Modules.BookingExperts.Data.PriceModelCurve.PricelistUsagesDTO;
import { useBaseStore } from "@/stores/base";
import { t } from "@/plugins/i18n";

const client = useApiClient("bookingExperts");
const { curve } = defineProps<{ curve: PriceModelCurveDO }>();
const visible = defineModel<boolean>("visible");
const usages = ref<PricelistUsagesDTO[]>([]);
const loading = ref(false);
const store = useBaseStore();

const hasMultipleAdministrations = computed(() => {
	return store.bexAdministrations.length > 1;
});

const groupedUsages = computed(() => {
	const groups = new Map<string, PricelistUsagesDTO[]>();

	for (const usage of usages.value) {
		const key = usage.administration_name;
		if (!groups.has(key)) {
			groups.set(key, []);
		}
		groups.get(key)!.push(usage);
	}
	return groups;
});

onMounted(async () => {
	loading.value = true;
	const response = await client.getPriceListForCurve(curve.id);

	if (response.isOk()) {
		usages.value = response.value;
	}
	loading.value = false;
});
</script>

<template>
	<Modal
		v-model:visible="visible"
		:title="`Pricelists using ${curve.name}`"
		icon="fa-solid fa-circle-info"
		width="35rem"
		hide-actions
	>
		<div v-if="loading" class="d-flex justify-center pa-4">
			<v-progress-circular indeterminate color="primary" />
		</div>

		<template v-else>
			<p
				v-if="usages.length === 0"
				class="text-medium-emphasis text-body-2 pa-4"
			>
				This curve is not used by any price list.
			</p>

			<v-table v-else density="compact">
				<thead>
					<tr>
						<th>Pricelist</th>
						<th class="text-right">Days used</th>
						<th class="text-right">Actions</th>
					</tr>
				</thead>
				<tbody>
					<template
						v-for="[administrationName, items] in groupedUsages"
						:key="administrationName"
					>
						<tr v-if="hasMultipleAdministrations" class="administration-header">
							<td colspan="3">{{ administrationName }}</td>
						</tr>
						<tr v-for="usage in items" :key="usage.name">
							<td class="pl-6">{{ usage.name }}</td>
							<td class="text-right">{{ usage.days_used }}</td>
							<td class="text-right">
								<v-tooltip :text="t('pricelist.index.table.tooltips.view')">
									<template #activator="{ props: tooltipProps }">
										<v-btn
											v-bind="tooltipProps"
											size="30"
											:to="`/pricing/apply/${usage.pricelist_id}`"
											color="primary"
											variant="text"
										>
											<v-icon size="14" icon="fa-light fa-calendars" />
										</v-btn>
									</template>
								</v-tooltip>
							</td>
						</tr>
					</template>
				</tbody>
			</v-table>
		</template>
	</Modal>
</template>

<style scoped lang="scss">
.administration-header td {
	font-weight: 600;
	font-size: 0.75rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: rgba(var(--v-theme-on-surface), 0.6);
	background: rgba(var(--color-background));
}
</style>
